import React, { useEffect, useState } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  TextField,
  FormControlLabel,
  Checkbox,
  Typography,
  Box,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider
} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import {
  type FamilyData,
  type FamilyMemberData,
  type ValidationError,
  buildFamilyString,
  parseFamilyString
} from './family-editor-dialog-types'

interface FamilyEditorDialogProps {
  open: boolean
  onClose: () => void
  onSave: (familyString: string) => void
  initialValue: string
}

interface FamilyMemberSectionProps {
  title: string
  data: FamilyMemberData | undefined
  onChange: (data: FamilyMemberData | undefined) => void
  errors: ValidationError[]
  memberKey: string
}

const FamilyMemberSection: React.FC<FamilyMemberSectionProps> = ({
  title,
  data,
  onChange,
  errors,
  memberKey
}) => {
  const handleNumberChange = (field: keyof FamilyMemberData, value: string): void => {
    const numValue = value === '' ? 0 : parseInt(value, 10)
    if (isNaN(numValue) || numValue < 0) return

    if (numValue === 0 && field === 'totalNumber') {
      onChange(undefined)
      return
    }

    const updatedData: FamilyMemberData = {
      totalNumber: 0,
      ...data,
      [field]: numValue
    }
    onChange(updatedData)
  }

  const handleCheckboxChange = (field: keyof FamilyMemberData, checked: boolean): void => {
    if (data == null) return
    onChange({ ...data, [field]: checked })
  }

  const getFieldError = (field: string): string | undefined => {
    const error = errors.find(e => e.field === `${memberKey}.${field}`)
    return error?.message
  }

  const hasData = (data != null) && data.totalNumber > 0

  return (
    <Accordion>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography variant="h6">
          {title} {hasData && `(${data.totalNumber})`}
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Total Number"
              type="number"
              value={data?.totalNumber ?? ''}
              onChange={(e) => {
                handleNumberChange('totalNumber', e.target.value)
              }}
              fullWidth
              inputProps={{ min: 0 }}
              error={getFieldError('totalNumber') != null}
              helperText={getFieldError('totalNumber') ?? ''}
            />
          </Grid>

          <Grid item xs={6} sm={3}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={data?.hasTwins === true}
                      onChange={(e) => {
                        handleCheckboxChange('hasTwins', e.target.checked)
                      }}
                      color={getFieldError('hasTwins') != null ? 'error' : 'primary'}
                    />
                  }
                  label="Has Twins"
                />
                {getFieldError('hasTwins') != null && (
                  <Typography variant="caption" color="error" display="block">
                    {getFieldError('hasTwins')}
                  </Typography>
                )}
              </Grid>

              <Grid item xs={6} sm={3}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={data?.hasTriplets === true}
                      onChange={(e) => {
                        handleCheckboxChange('hasTriplets', e.target.checked)
                      }}
                      color={getFieldError('hasTriplets') != null ? 'error' : 'primary'}
                    />
                  }
                  label="Has Triplets"
                />
                {getFieldError('hasTriplets') != null && (
                  <Typography variant="caption" color="error" display="block">
                    {getFieldError('hasTriplets')}
                  </Typography>
                )}
              </Grid>

          {hasData && (
            <>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="From Previous Marriage"
                  type="number"
                  value={data?.fromPreviousMarriage ?? ''}
                  onChange={(e) => {
                    handleNumberChange('fromPreviousMarriage', e.target.value)
                  }}
                  fullWidth
                  inputProps={{ min: 0, max: data?.totalNumber }}
                  error={getFieldError('fromPreviousMarriage') != null}
                  helperText={getFieldError('fromPreviousMarriage') ?? ''}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label="Deceased"
                  type="number"
                  value={data?.deceased ?? ''}
                  onChange={(e) => {
                    handleNumberChange('deceased', e.target.value)
                  }}
                  fullWidth
                  inputProps={{ min: 0, max: data?.totalNumber }}
                  error={getFieldError('deceased') != null}
                  helperText={getFieldError('deceased') ?? ''}
                />
              </Grid>

            </>
          )}
        </Grid>
      </AccordionDetails>
    </Accordion>
  )
}

export const FamilyEditorDialog: React.FC<FamilyEditorDialogProps> = ({
  open,
  onClose,
  onSave,
  initialValue
}) => {
  const [familyData, setFamilyData] = useState<FamilyData>({})
  const [familyString, setFamilyString] = useState('')
  const [errors, setErrors] = useState<ValidationError[]>([])

  // Initialize from existing family string
  useEffect(() => {
    if (open) {
      if (initialValue.length > 0) {
        // Parse the existing family string to populate form fields
        const parsedData = parseFamilyString(initialValue)
        setFamilyData(parsedData)
      } else {
        setFamilyData({})
      }
    }
  }, [open, initialValue])

  // Update family string whenever data changes
  useEffect(() => {
    const result = buildFamilyString(familyData)
    setFamilyString(result.familyString)
    setErrors(result.errors)
  }, [familyData])

  const updateFamilyMember = (
    memberType: keyof FamilyData,
    data: FamilyMemberData | undefined
  ): void => {
    setFamilyData(prev => {
      if (data != null) {
        return { ...prev, [memberType]: data }
      } else {
        const { [memberType]: _, ...updated } = prev
        return updated
      }
    })
  }

  const handleSave = (): void => {
    if (errors.length === 0) {
      onSave(familyString)
    }
  }

  const handleClose = (): void => {
    setFamilyData({})
    setFamilyString('')
    setErrors([])
    onClose()
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          height: '90vh',
          width: '90vw'
        }
      }}
    >
      <DialogTitle>Family Editor</DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle1" gutterBottom>
            Preview:
          </Typography>
          <TextField
            value={familyString}
            fullWidth
            multiline
            rows={2}
            variant="outlined"
            InputProps={{ readOnly: true }}
            sx={{ mb: 1 }}
          />
          <Typography variant="caption" color="textSecondary">
            {familyString.length}/200 characters
          </Typography>
        </Box>

        {errors.length > 0 && (
          <Box sx={{ mb: 2 }}>
            {errors.map((error, index) => (
              <Alert key={index} severity={error.field === 'familyString' ? 'warning' : 'error'} sx={{ mb: 1 }}>
                {error.message}
              </Alert>
            ))}
          </Box>
        )}

        <Divider sx={{ mb: 2 }} />

        <FamilyMemberSection
          title="Children"
          data={familyData.children}
          onChange={(data) => {
            updateFamilyMember('children', data)
          }}
          errors={errors}
          memberKey="children"
        />

        <FamilyMemberSection
          title="Stepchildren"
          data={familyData.stepchildren}
          onChange={(data) => {
            updateFamilyMember('stepchildren', data)
          }}
          errors={errors}
          memberKey="stepchildren"
        />

        <FamilyMemberSection
          title="Grandchildren"
          data={familyData.grandchildren}
          onChange={(data) => {
            updateFamilyMember('grandchildren', data)
          }}
          errors={errors}
          memberKey="grandchildren"
        />

        <FamilyMemberSection
          title="Great-Grandchildren"
          data={familyData.greatGrandchildren}
          onChange={(data) => {
            updateFamilyMember('greatGrandchildren', data)
          }}
          errors={errors}
          memberKey="greatGrandchildren"
        />

        <FamilyMemberSection
          title="Adopted Children"
          data={familyData.adoptedChildren}
          onChange={(data) => {
            updateFamilyMember('adoptedChildren', data)
          }}
          errors={errors}
          memberKey="adoptedChildren"
        />

        <FamilyMemberSection
          title="Foster Children"
          data={familyData.fosterChildren}
          onChange={(data) => {
            updateFamilyMember('fosterChildren', data)
          }}
          errors={errors}
          memberKey="fosterChildren"
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="secondary">
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          color="primary"
          variant="contained"
          disabled={errors.length > 0}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  )
}
